import { OBLIGATION_PHASES } from './obligationPhases';
import { isPlatformAdmin, ObligationAuthError } from '@/shared/auth/obligationAuth';
import { PROPOSAL_STATUS_OPTIONS } from '@/shared/utils/domain/statusUtils';

/**
 * Dozvoljene ciljne statuse obaveze (samo ADMIN koristi status/update).
 * Ključ = trenutni status.
 * Redosled = redosled u dropdown-u. Iz ACCEPTED / PARTIALLY_ACCEPTED / REJECTED samo povratak na UNDER_REVIEW,
 * osim IN_IMPLEMENTATION koji se nudi samo kada je AP u sprovođenju (vidi {@link ACTION_PLAN_GATED_TARGETS}).
 * INAPPROPRIATE samo iz CREATED; REJECTED tek iz UNDER_REVIEW. REJECTED_AFTER_REVIEW se više ne bira (ostaje samo izlaz za stare zapise).
 */
export const OBLIGATION_STATUS_TRANSITIONS = {
  CREATED: ['UNDER_REVIEW', 'INAPPROPRIATE'],
  UNDER_REVIEW: ['ACCEPTED', 'PARTIALLY_ACCEPTED', 'REJECTED'],
  ACCEPTED: ['IN_IMPLEMENTATION', 'UNDER_REVIEW'],
  PARTIALLY_ACCEPTED: ['IN_IMPLEMENTATION', 'UNDER_REVIEW'],
  REJECTED: ['UNDER_REVIEW'],
  INAPPROPRIATE: ['CREATED'],
  REJECTED_AFTER_REVIEW: ['UNDER_REVIEW'],
  IN_IMPLEMENTATION: [],
};

/** Ciljni status → status AP-a u kome je dozvoljen. */
const ACTION_PLAN_GATED_TARGETS = {
  IN_IMPLEMENTATION: 'IMPLEMENTATION',
};

/**
 * Ciljni statusi koje admin sme izabrati iz dropdown-a (bez trenutnog).
 * @param {string} currentStatus
 * @param {{ actionPlanStatus?: string }} [options]
 * @returns {string[]}
 */
export function getAllowedObligationStatusTargets(currentStatus, { actionPlanStatus = '' } = {}) {
  const from = String(currentStatus ?? '').trim().toUpperCase();
  if (!from) {
    return PROPOSAL_STATUS_OPTIONS.filter((s) => s === 'CREATED');
  }
  const targets = OBLIGATION_STATUS_TRANSITIONS[from];
  if (!Array.isArray(targets) || targets.length === 0) return [];
  const apStatus = String(actionPlanStatus ?? '').trim().toUpperCase();
  return targets.filter((s) => {
    if (!PROPOSAL_STATUS_OPTIONS.includes(s)) return false;
    const requiredApStatus = ACTION_PLAN_GATED_TARGETS[s];
    return !requiredApStatus || requiredApStatus === apStatus;
  });
}

/**
 * Servisna granica za POST /obligation/status/update — ista matrica kao UI dropdown.
 * @param {string} fromStatus
 * @param {string} toStatus
 * @param {{ actionPlanStatus?: string }} [options]
 */
export function assertValidObligationStatusTransition(fromStatus, toStatus, options = {}) {
  const from = String(fromStatus ?? '').trim().toUpperCase();
  const to = String(toStatus ?? '').trim().toUpperCase();
  if (!to) {
    throw new ObligationAuthError('Изабрана промена статуса обавезе није дозвољена.');
  }
  const allowed = getAllowedObligationStatusTargets(from, options);
  if (!allowed.includes(to)) {
    throw new ObligationAuthError('Изабрана промена статуса обавезе није дозвољена.');
  }
}

/**
 * Odvojeno od {@link resolveObligationFieldAccess} — `canSubmit` на sekciji ≠ promena statusa obaveze.
 * @returns {{ canChangeStatus: boolean, allowedStatuses: string[] }}
 */
export function resolveObligationStatusChangeAccess({
  currentUser = null,
  roleSet = null,
  isAdminContext: _isAdminContext = false,
  aclProfile: _aclProfile = 'default',
  obligationStatus = '',
  actionPlanStatus = '',
}) {
  const roles = roleSet || new Set();
  const isAdminRole = isPlatformAdmin(currentUser) || roles.has('ADMIN');
  if (!isAdminRole) {
    return { canChangeStatus: false, allowedStatuses: [] };
  }

  // Status chip prati OBLIGATION_STATUS_TRANSITIONS; uređivanje polja forme ostaje
  // ograničeno na CREATED / UNDER_REVIEW preko isAdminObligationFormStatusEditable.
  const allowedStatuses = getAllowedObligationStatusTargets(obligationStatus, { actionPlanStatus });
  return {
    canChangeStatus: allowedStatuses.length > 0,
    allowedStatuses,
  };
}

/**
 * На /admin/obaveza/ админско уређивање развоја обавезе дозвољено је само за ове статусе.
 * Празан статус (нпр. нова обавеза пре првог чувања) третира се као уредив.
 */
export const ADMIN_OBLIGATION_FORM_EDITABLE_STATUSES = new Set(['CREATED', 'UNDER_REVIEW']);

export function isAdminObligationFormStatusEditable(obligationStatus) {
  const s = String(obligationStatus ?? '').trim().toUpperCase();
  if (!s) return true;
  return ADMIN_OBLIGATION_FORM_EDITABLE_STATUSES.has(s);
}

/** Где се користи ACL за обавезу (развој обрасца vs извештај о спровођењу). */
export const OBLIGATION_FORM_SURFACE = {
  DEVELOPMENT: 'development',
  IMPLEMENTATION_REPORT: 'implementation_report',
};

export const OBLIGATION_SECTIONS = {
  GENERAL: 'GENERAL',
  PROPOSAL: 'PROPOSAL',
  PROBLEM_DEFINITION: 'PROBLEM_DEFINITION',
  OBLIGATION_DESCRIPTION: 'OBLIGATION_DESCRIPTION',
  ANALYSIS: 'ANALYSIS',
  PLANNING: 'PLANNING',
  INSTITUTIONS: 'INSTITUTIONS',
  MODERATORS: 'MODERATORS',
  PARTNERS: 'PARTNERS',
};

const DEFAULT_MATRIX = {
  [OBLIGATION_PHASES.PROPOSAL]: {
    editable: new Set([
      OBLIGATION_SECTIONS.GENERAL,
      OBLIGATION_SECTIONS.PROPOSAL,
      OBLIGATION_SECTIONS.PROBLEM_DEFINITION,
      OBLIGATION_SECTIONS.OBLIGATION_DESCRIPTION,
      OBLIGATION_SECTIONS.ANALYSIS,
      OBLIGATION_SECTIONS.PARTNERS,
      OBLIGATION_SECTIONS.INSTITUTIONS,
    ]),
    /**
     * Status CREATED: nema usaglašavanja; admin može da koriguje predloge pre slanja na UNDER_REVIEW.
     * Proposer/moderator dobijaju mogućnost izmene tek nakon prelaska u ALIGNMENT (UNDER_REVIEW).
     */
    roles: new Set(['ADMIN']),
  },
  [OBLIGATION_PHASES.ALIGNMENT]: {
    editable: new Set([
      OBLIGATION_SECTIONS.PROPOSAL,
      OBLIGATION_SECTIONS.PROBLEM_DEFINITION,
      OBLIGATION_SECTIONS.OBLIGATION_DESCRIPTION,
      OBLIGATION_SECTIONS.ANALYSIS,
      // Allow proposing/updating planning activities during UNDER_REVIEW consensus.
      OBLIGATION_SECTIONS.PLANNING,
      OBLIGATION_SECTIONS.MODERATORS,
    ]),
    roles: new Set(['ADMIN', 'MODERATOR', 'PROPOSER', 'TEAM_LEAD']),
  },
  [OBLIGATION_PHASES.PLANNING]: {
    editable: new Set([
      OBLIGATION_SECTIONS.PLANNING,
      OBLIGATION_SECTIONS.PARTNERS,
      OBLIGATION_SECTIONS.INSTITUTIONS,
    ]),
    roles: new Set(['ADMIN', 'MODERATOR', 'TEAM_LEAD']),
  },
};

export const resolveObligationFieldAccess = ({
  phase,
  roleSet,
  sectionKey,
  isOwner = false,
  isModerator = false,
  isResponsibleInstitutionLead = false,
  aclProfile = 'default',
  obligationStatus = '',
  obligationFormSurface = OBLIGATION_FORM_SURFACE.DEVELOPMENT,
  actionPlanStatus = '',
}) => {
  /**
   * На /profil/ подразумевано је read-only осим:
   * - ADMIN + CREATED → уређивање предлога;
   * - UNDER_REVIEW табови за усаглашавање → матрица (предлагач/модератор…) и активности са consensus UI.
   */
  const roles = roleSet || new Set();
  const isAdmin = roles.has('ADMIN');
  const statusNorm = String(obligationStatus || '').trim().toUpperCase();
  const isProfileCreatedAdminBypass =
    aclProfile === 'profile'
    && isAdmin
    && statusNorm === 'CREATED';

  /** На профилу: UNDER_REVIEW (усаглашавање) — одговарајући табови иду кроз матрицу, не закључај се унапред. */
  const profileUnderReviewEditableSections = new Set([
    OBLIGATION_SECTIONS.PROBLEM_DEFINITION,
    OBLIGATION_SECTIONS.OBLIGATION_DESCRIPTION,
    OBLIGATION_SECTIONS.ANALYSIS,
    OBLIGATION_SECTIONS.PLANNING,
    OBLIGATION_SECTIONS.MODERATORS,
  ]);
  const isProfileUnderReviewBypass =
    aclProfile === 'profile'
    && phase === OBLIGATION_PHASES.ALIGNMENT
    && statusNorm === 'UNDER_REVIEW'
    && profileUnderReviewEditableSections.has(sectionKey);

  const apStatusNormEarly = String(actionPlanStatus ?? '').trim().toUpperCase();
  /** /profil/pracenje-sprovodjenja: модератор снима извештај (ук. „Спровођење обавезе”) док је АП у IMPLEMENTATION. */
  const profileImplementationReportModeratorGeneral =
    aclProfile === 'profile'
    && isModerator
    && obligationFormSurface === OBLIGATION_FORM_SURFACE.IMPLEMENTATION_REPORT
    && apStatusNormEarly === 'IMPLEMENTATION'
    && sectionKey === OBLIGATION_SECTIONS.GENERAL;

  if (profileImplementationReportModeratorGeneral) {
    return { canView: true, canEdit: true, canSubmit: true, readOnlyReason: '' };
  }

  const isProfileLocked =
    aclProfile === 'profile'
    && !isProfileCreatedAdminBypass
    && !isProfileUnderReviewBypass;

  const selectedPhase = DEFAULT_MATRIX[phase] ? phase : OBLIGATION_PHASES.PROPOSAL;
  const matrix = DEFAULT_MATRIX[selectedPhase];

  if (isProfileLocked) {
    return { canView: true, canEdit: false, canSubmit: true, readOnlyReason: 'Поље је закључано у профил режиму.' };
  }

  const apStatusNorm = apStatusNormEarly;
  /**
   * Извештај на /admin/izvestaj/ док је АП у IMPLEMENTATION — админ може да уноси и шаље извештај.
   */
  if (
    aclProfile === 'default'
    && isAdmin
    && obligationFormSurface === OBLIGATION_FORM_SURFACE.IMPLEMENTATION_REPORT
    && apStatusNorm === 'IMPLEMENTATION'
  ) {
    return { canView: true, canEdit: true, canSubmit: true, readOnlyReason: '' };
  }

  /**
   * Admin на /admin/obaveza/: без глобалног bypass-а — поља само за CREATED и UNDER_REVIEW.
   * На профилу (aclProfile profile) правила изнад и даље важе.
   */
  if (
    aclProfile === 'default'
    && isAdmin
    && !isAdminObligationFormStatusEditable(obligationStatus)
  ) {
    return {
      canView: true,
      canEdit: false,
      canSubmit: false,
      readOnlyReason:
        'Уређивање је могуће само за обавезе у статусима „Предложена“ и „Узета у разматрање“.',
    };
  }

  if (isAdmin) {
    return { canView: true, canEdit: true, canSubmit: true, readOnlyReason: '' };
  }

  let hasAllowedRole = false;
  if (isOwner) {
    hasAllowedRole = matrix.roles.has('PROPOSER');
  }
  if (isModerator) {
    hasAllowedRole = hasAllowedRole || matrix.roles.has('MODERATOR');
  }
  if (isResponsibleInstitutionLead) {
    hasAllowedRole = hasAllowedRole || matrix.roles.has('TEAM_LEAD');
  }
  if (!hasAllowedRole) {
    hasAllowedRole = [...roles].some((role) => matrix.roles.has(role));
  }

  const canEdit = hasAllowedRole && matrix.editable.has(sectionKey);
  const readOnlyReason = canEdit ? '' : 'Поље је закључано за тренутну фазу или улогу.';

  return {
    canView: true,
    canEdit,
    canSubmit: hasAllowedRole,
    readOnlyReason,
  };
};
